/**
 * shared/space_lod.ts — CLIENT MIRROR of server/src/lib/space_lod.ts (the
 * server copy is authoritative; keep them byte-identical below the headers).
 * Space Wave 6 — one state at four resolutions: the resolution bands, the
 * den's one closed-form physics, the den lists. Pure.
 */
import type { SystemSector } from './galaxy';

// ── THE RESOLUTION LAW ───────────────────────────────────────────────────────
export const LOD_L1_HYST_S = 300;
export const LOD_L2_INTEL_S = 7_200;
export type Lod = 0 | 1 | 2 | 3;
/** L0 a pilot is here · L1 one was within the hysteresis · L2 fresh intel · L3 cold. */
export function lodFor(observers: number, sSincePresence: number, sSinceIntel: number): Lod {
  if (observers > 0) return 0;
  if (sSincePresence < LOD_L1_HYST_S) return 1;
  if (sSinceIntel < LOD_L2_INTEL_S) return 2;
  return 3;
}
export const LOD_NAME: Record<Lod, string> = { 0: 'L0 — simulated hull by hull (a pilot is here)', 1: 'L1 — held warm (a pilot just left)', 2: 'L2 — advanced on demand (fresh intel)', 3: 'L3 — cold' };

// ── THE ONE-STATE LAW ────────────────────────────────────────────────────────
export const DEN_RESPAWN_S = 120;
/** The den's ONE physics: a hull per DEN_RESPAWN_S of elapsed time, capped; the anchor moves by the whole periods used. */
export function denRespawn(alive: number, capacity: number, anchorMicros: bigint, nowMicros: bigint): { alive: number; anchor: bigint } {
  if (alive >= capacity) return { alive: capacity, anchor: nowMicros };
  const periodUs = BigInt(DEN_RESPAWN_S) * 1_000_000n;
  const elapsed = nowMicros - anchorMicros;
  if (elapsed < periodUs) return { alive, anchor: anchorMicros };
  const n = Number(elapsed / periodUs);
  const next = Math.min(capacity, alive + n);
  const used = next - alive;
  return { alive: next, anchor: next >= capacity ? nowMicros : anchorMicros + BigInt(used) * periodUs };
}

export interface DenSpec { key: string; capacity: number; tier: 1 | 2 | 3; pos: [number, number, number] }
/** The Commons POIs with pirates, in space_sector.ts order (the harness pins them against POIS). */
export const SYS0_DENS: readonly DenSpec[] = [
  { key: 'poi1', capacity: 3, tier: 1, pos: [-320_000, -26_000, 430_000] },
  { key: 'poi2', capacity: 4, tier: 2, pos: [540_000, -42_000, -490_000] },
  { key: 'poi3', capacity: 3, tier: 2, pos: [-620_000, 90_000, -360_000] },
  { key: 'poi5', capacity: 2, tier: 2, pos: [-420_000, 60_000, -520_000] },
  { key: 'poi6', capacity: 2, tier: 2, pos: [560_000, -120_000, 900_000] },
  { key: 'poi7', capacity: 3, tier: 2, pos: [-700_000, 180_000, 300_000] },
  { key: 'poi8', capacity: 3, tier: 2, pos: [300_000, -40_000, -350_000] },
  { key: 'poi9', capacity: 6, tier: 3, pos: [-1_050_000, 320_000, 850_000] },
  { key: 'poi10', capacity: 1, tier: 1, pos: [820_000, 90_000, 120_000] },
  { key: 'poi13', capacity: 2, tier: 1, pos: [-14_000, -700, 11_500] },
  { key: 'poi14', capacity: 2, tier: 1, pos: [18_500, -1_400, -13_000] },
  { key: 'poi15', capacity: 1, tier: 1, pos: [-9_500, 1_800, -12_500] },
  { key: 'poi16', capacity: 3, tier: 2, pos: [-150_000, 40_000, 1_150_000] },
  { key: 'poi17', capacity: 3, tier: 2, pos: [950_000, -180_000, 450_000] },
  { key: 'poi18', capacity: 3, tier: 3, pos: [-1_350_000, -60_000, -150_000] },
  { key: 'poi19', capacity: 3, tier: 2, pos: [200_000, 260_000, 1_500_000] },
  { key: 'poi20', capacity: 3, tier: 3, pos: [1_400_000, 60_000, 300_000] },
  { key: 'poi23', capacity: 2, tier: 2, pos: [880_000, -60_000, -1_150_000] },
  { key: 'poi24', capacity: 2, tier: 2, pos: [-350_000, -240_000, 1_400_000] },
  { key: 'poi25', capacity: 3, tier: 2, pos: [-1_500_000, 220_000, 500_000] },
  { key: 'poi26', capacity: 3, tier: 2, pos: [1_200_000, 300_000, -300_000] },
  { key: 'poi27', capacity: 6, tier: 3, pos: [700_000, -320_000, -1_350_000] },
  { key: 'poi28', capacity: 1, tier: 1, pos: [-550_000, 340_000, -1_100_000] },
];
/** A system's dens: the Commons list, or a derived system's dens keyed by their bounty slot (poi<poiSlot>). */
export function densOf(systemId: string, sector: SystemSector | null): DenSpec[] {
  if (systemId === 'sys0') return [...SYS0_DENS];
  if (!sector || !/^sys\d{1,3}$/.test(systemId)) return [];
  return sector.dens.map((d) => ({ key: `poi${d.poiSlot}`, capacity: d.count, tier: d.tier, pos: [d.pos[0], d.pos[1], d.pos[2]] as [number, number, number] }));
}
/** Dens nearest a point first (THE RAIDER LAW picks the first with raiders). */
export function densByDistance(specs: readonly DenSpec[], x: number, y: number, z: number): DenSpec[] {
  const d2 = (s: DenSpec): number => (s.pos[0] - x) ** 2 + (s.pos[1] - y) ** 2 + (s.pos[2] - z) ** 2;
  return [...specs].sort((a, b) => d2(a) - d2(b));
}
// ── THE DEN SLOT LAW (shared choreography: hp + target; where a hull flies stays the client's) ──
/** A den slot's hit points by tier (the client's TIER_STATS.hp, in bolt units). */
export const DEN_SLOT_HP: Record<1 | 2 | 3, number> = { 1: 3, 2: 4, 3: 5 };
/** A blow on a den slot lands only from inside this of the den (the pack hunts near home). */
export const DEN_ENGAGE_M = 40_000;
/** The largest single blow a client may report (a missile on a heavy fit); more is a forged kill. */
export const DEN_HIT_MAX = 12;
/** A slot nobody has touched for this long heals and forgets its target (the row is dropped). */
export const DEN_SLOT_RESET_S = 120;
export const DEN_SLOT_KEY_RE = /^poi\d{1,2}:pirate\d{1,2}$/;
/** The dens whose first slot is a named ACE (the client's ACE_NAMES) and its hp multiple. */
export const ACE_DENS: readonly string[] = ['poi9', 'poi18', 'poi20', 'poi27'];
export const DEN_ACE_HP_MULT = 2.5;
export function denSlotHp(tier: number, denKey = '', slot = -1): number {
  const base = DEN_SLOT_HP[(tier === 1 || tier === 2 || tier === 3 ? tier : 2) as 1 | 2 | 3];
  return slot === 0 && ACE_DENS.includes(denKey) ? base * DEN_ACE_HP_MULT : base;
}
// ── THE DEN POSITION LAW (a shared position layer) ──────────────────────────
/** A patrolling den hull loiters a circuit every client computes alike from the
 *  WALL clock: the same point on every screen (steering lag aside). Radius 160 m,
 *  0.06 rad/s, a per-slot phase — the old private circuit made shared. */
export const DEN_PATROL_R_M = 160;
export const DEN_PATROL_RATE = 0.06;
export function denPatrolPoint(home: readonly [number, number, number], slot: number, denKey: string, nowMs: number): [number, number, number] {
  const t = nowMs / 1000 * DEN_PATROL_RATE + (slot * 7 + denKey.length);
  return [home[0] + Math.cos(t) * DEN_PATROL_R_M, home[1] + 0.12 * Math.sin(t * 0.7) * DEN_PATROL_R_M, home[2] + Math.sin(t) * DEN_PATROL_R_M];
}
/** An ENGAGED hull is flown by the pilot it hunts (den_slot.target) and STREAMED to everyone else
 *  (den_hull): one writer per hull, fenced by the slot's target; a stale row is dropped. */
export const DEN_HULL_STREAM_MS = 333;
export const DEN_HULL_STALE_S = 12;
/** A streamed seat may not leave the den's hunting ground. */
export const DEN_HULL_RANGE_M = DEN_ENGAGE_M + 10_000;
/** poi<slot>:pirate<i> → the den key and the slot index. */
export function denKeyOf(pirateKey: string): string { return pirateKey.split(':')[0]; }
export function slotOf(pirateKey: string): number { const m = /pirate(\d+)$/.exec(pirateKey); return m ? Number(m[1]) : -1; }
